import type { MapSnapshot } from '@/model/types';

/**
 * Parse and validate a JSON string as a MapSnapshot.
 *
 * Performs basic structural validation to catch obviously malformed data.
 */
export function parseJson(jsonString: string): MapSnapshot {
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonString) as unknown;
  } catch {
    throw new Error('Invalid JSON: failed to parse');
  }

  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('Invalid MapSnapshot: expected a JSON object');
  }

  const obj = parsed as Record<string, unknown>;

  if (!obj['meta'] || typeof obj['meta'] !== 'object') {
    throw new Error('Invalid MapSnapshot: missing "meta" object');
  }

  if (!obj['nodes'] || typeof obj['nodes'] !== 'object') {
    throw new Error('Invalid MapSnapshot: missing "nodes" object');
  }

  const meta = obj['meta'] as Record<string, unknown>;
  if (typeof meta['rootNodeId'] !== 'string' || !meta['rootNodeId']) {
    throw new Error('Invalid MapSnapshot: meta.rootNodeId is required');
  }

  if (typeof meta['title'] !== 'string') {
    throw new Error('Invalid MapSnapshot: meta.title is required');
  }

  const nodes = obj['nodes'] as Record<string, unknown>;
  const rootNodeId = meta['rootNodeId'] as string;
  if (!(rootNodeId in nodes)) {
    throw new Error(`Invalid MapSnapshot: root node "${rootNodeId}" not found in nodes`);
  }

  // Fill in defaults for optional top-level fields
  const snapshot: MapSnapshot = {
    meta: {
      title: (meta['title'] as string) ?? 'Untitled',
      rootNodeId,
      createdAt: (meta['createdAt'] as string) ?? new Date().toISOString(),
      updatedAt: (meta['updatedAt'] as string) ?? new Date().toISOString(),
    },
    settings: (obj['settings'] as MapSnapshot['settings']) ?? {
      layoutType: 'horizontal-tree' as const,
      horizontalGap: 80,
      verticalGap: 16,
      themeId: 'default',
    },
    nodes: nodes as MapSnapshot['nodes'],
    connections: (obj['connections'] as MapSnapshot['connections']) ?? {},
  };

  return snapshot;
}
